import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Calendar, AlertTriangle } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function Contact() {
  return <div className="w-full bg-dark min-h-screen pb-24" data-id="element-114">
      {/* Header */}
      <section className="bg-dark py-20 border-b-4 border-accent relative overflow-hidden" data-id="element-115">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-darkSlate via-dark to-dark opacity-80" data-id="element-116"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-117">
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-5xl md:text-6xl font-heading font-bold text-white uppercase tracking-tight" data-id="element-118">
            Contact <span className="text-accent" data-id="element-119">[CLINIC NAME]</span>
          </motion.h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-id="element-120">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-121">
          {/* Left Column: Info & Hours */}
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="w-full lg:w-1/3 space-y-8" data-id="element-122">
            {/* Info Block */}
            <div className="bg-darkSlate p-8 rounded-md shadow-lg border border-darkCard" data-id="element-123">
              <h2 className="text-2xl font-heading font-bold text-white uppercase tracking-wider mb-6 border-b border-darkCard pb-4" data-id="element-124">
                Clinic Information
              </h2>

              <div className="space-y-6" data-id="element-125">
                <div data-id="element-126">
                  <h3 className="font-bold text-white mb-2 text-lg" data-id="element-127">
                    [CLINIC NAME]
                  </h3>
                  <div className="flex items-start text-slate-400 mt-2" data-id="element-128">
                    <MapPin className="w-5 h-5 mr-4 text-accent flex-shrink-0 mt-0.5" data-id="element-129" />
                    <span className="leading-relaxed" data-id="element-130">
                      Serving patients via telehealth in [STATE]
                    </span>
                  </div>
                </div>

                <div className="flex items-center text-slate-400" data-id="element-131">
                  <Phone className="w-5 h-5 mr-4 text-accent flex-shrink-0" data-id="element-132" />
                  <span className="font-medium text-white" data-id="element-133">[PHONE]</span>
                </div>

                <div className="flex items-center text-slate-400" data-id="element-134">
                  <Mail className="w-5 h-5 mr-4 text-accent flex-shrink-0" data-id="element-135" />
                  <span className="font-medium text-white" data-id="element-136">[EMAIL]</span>
                </div>
              </div>
            </div>

            {/* Hours Block */}
            <div className="bg-darkSlate p-8 rounded-md shadow-lg border border-darkCard" data-id="element-137">
              <h2 className="text-2xl font-heading font-bold text-white uppercase tracking-wider mb-6 border-b border-darkCard pb-4 flex items-center" data-id="element-138">
                <Clock className="w-6 h-6 mr-3 text-accent" data-id="element-139" />
                Hours of Operation
              </h2>

              <ul className="space-y-4 text-slate-400" data-id="element-140">
                <li className="flex justify-between items-center" data-id="element-141">
                  <span className="font-medium text-white" data-id="element-142">
                    Monday - Friday
                  </span>
                  <span data-id="element-143">8:00 AM - 6:00 PM</span>
                </li>
                <li className="flex justify-between items-center" data-id="element-144">
                  <span className="font-medium text-white" data-id="element-145">Saturday</span>
                  <span data-id="element-146">9:00 AM - 1:00 PM</span>
                </li>
                <li className="flex justify-between items-center text-slate-500" data-id="element-147">
                  <span className="font-medium" data-id="element-148">Sunday</span>
                  <span data-id="element-149">Closed</span>
                </li>
              </ul>
              <p className="text-xs text-accent mt-8 pt-6 border-t border-darkCard italic uppercase tracking-wider font-bold" data-id="element-150">
                * Telehealth appointments must be scheduled in advance.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Booking & Form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="w-full lg:w-2/3 space-y-8" data-id="element-151">
            {/* Dummy Booking Widget */}
            <div className="bg-darkSlate p-8 md:p-10 rounded-md shadow-xl border border-darkCard" data-id="element-152">
              <h2 className="text-3xl font-heading font-bold text-white uppercase tracking-tight mb-8" data-id="element-153">
                Schedule your tele-health appointment
              </h2>

              <div className="bg-dark border border-dashed border-darkCard rounded-md p-8 text-center mb-10 relative overflow-hidden" data-id="element-154">
                <div className="absolute inset-0 bg-dark/80 flex items-center justify-center backdrop-blur-sm z-20" data-id="element-155">
                  <div className="bg-darkSlate px-8 py-4 rounded-md shadow-2xl border border-accent/30 flex items-center" data-id="element-156">
                    <Calendar className="w-6 h-6 text-accent mr-3" data-id="element-157" />
                    <span className="font-bold text-white text-sm uppercase tracking-widest" data-id="element-158">
                      Placeholder telehealth booking widget
                    </span>
                  </div>
                </div>

                {/* Fake calendar UI underneath */}
                <div className="opacity-30 pointer-events-none relative z-10" data-id="element-159">
                  <div className="flex justify-between items-center mb-6" data-id="element-160">
                    <div className="font-heading font-bold text-xl text-white" data-id="element-161">
                      October 2023
                    </div>
                    <div className="flex space-x-2" data-id="element-162">
                      <div className="w-8 h-8 bg-darkCard rounded" data-id="element-163"></div>
                      <div className="w-8 h-8 bg-darkCard rounded" data-id="element-164"></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-2 mb-4" data-id="element-165">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className="text-xs font-bold text-slate-500" data-id="element-166">
                        {d}
                      </div>)}
                    {Array.from({
                    length: 31
                  }).map((_, i) => <div key={i} className={`h-12 rounded flex items-center justify-center text-sm font-medium ${i === 14 ? 'bg-accent text-white' : 'bg-darkCard text-slate-400 border border-darkCard'}`} data-id="element-167">
                        {i + 1}
                      </div>)}
                  </div>
                </div>

                <p className="text-xs text-slate-500 mt-6 relative z-30 uppercase tracking-wider" data-id="element-168">
                  To be replaced with secure platform embed (e.g., Jane, Kareo,
                  SimplePractice)
                </p>
              </div>

              {/* Minimal Contact Form */}
              <div className="border-t border-darkCard pt-10" data-id="element-169">
                <h3 className="text-2xl font-heading font-bold text-white uppercase tracking-wider mb-8" data-id="element-170">
                  Or send us a general inquiry
                </h3>

                <form className="space-y-6" onSubmit={e => e.preventDefault()} data-id="element-171">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-172">
                    <div data-id="element-173">
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3" data-id="element-174">
                        Name
                      </label>
                      <input type="text" className="w-full px-5 py-4 rounded-md border border-darkCard focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-dark text-white placeholder-slate-600" placeholder="John Doe" data-id="element-175" />
                    </div>
                    <div data-id="element-176">
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3" data-id="element-177">
                        Email
                      </label>
                      <input type="email" className="w-full px-5 py-4 rounded-md border border-darkCard focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-dark text-white placeholder-slate-600" placeholder="john@example.com" data-id="element-178" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-179">
                    <div data-id="element-180">
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3" data-id="element-181">
                        Phone
                      </label>
                      <input type="tel" className="w-full px-5 py-4 rounded-md border border-darkCard focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-dark text-white placeholder-slate-600" placeholder="(555) 123-4567" data-id="element-182" />
                    </div>
                    <div data-id="element-183">
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3" data-id="element-184">
                        Preferred Time
                      </label>
                      <select className="w-full px-5 py-4 rounded-md border border-darkCard focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-dark text-white appearance-none" data-id="element-185">
                        <option data-id="element-186">Morning (8am - 12pm)</option>
                        <option data-id="element-187">Afternoon (12pm - 4pm)</option>
                        <option data-id="element-188">Evening (4pm - 6pm)</option>
                      </select>
                    </div>
                  </div>

                  <div data-id="element-189">
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3" data-id="element-190">
                      How can we help?
                    </label>
                    <textarea rows={4} className="w-full px-5 py-4 rounded-md border border-darkCard focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all bg-dark text-white placeholder-slate-600 resize-none" placeholder="I'm interested in learning more about TRT..." data-id="element-191"></textarea>
                    <div className="flex items-start mt-4 text-accent bg-accent/10 p-4 rounded-md border border-accent/20" data-id="element-192">
                      <AlertTriangle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" data-id="element-193" />
                      <p className="text-sm font-medium leading-relaxed" data-id="element-194">
                        Please do not include medical details, diagnoses, test
                        results, or prescription information in this form.
                      </p>
                    </div>
                  </div>

                  <button type="button" className="w-full bg-accent hover:bg-accentHover text-white font-bold py-5 px-6 rounded-md transition-all duration-300 shadow-[0_0_15px_rgba(255,107,53,0.2)] hover:shadow-[0_0_25px_rgba(255,107,53,0.4)] hover:-translate-y-1 uppercase tracking-widest mt-4" data-id="element-195">
                    Send Message
                  </button>
                </form>
              </div>
            </div>

            {/* HIPAA Notice */}
            <div className="bg-darkSlate p-6 rounded-md border border-darkCard text-center shadow-lg" data-id="element-196">
              <p className="text-sm text-slate-400 font-medium leading-relaxed" data-id="element-197">
                Protected health information is only collected and stored in our
                secure telehealth system, not on this website.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}